import type { CancelAction } from './cancel-action.type.js';
import type { CancelState } from './cancel-state.type.js';
import { BusinessErrorReason } from './business-error-reason.js';
import { TechnicalErrorVariant } from '../technical-error-variant.js';
import type { AppointmentInfo } from '../../token-module/appointment-info.interface.js';

/**
 * Pure Reducer der 5-Screen-State-Machine.
 *
 * State-Diagramm (erlaubte Transitions):
 *
 * ```
 *  *  ──TOKEN_PARSED──────────▶ START (erster Parse / Technical-Error)
 *  *  ──TOKEN_PARSED──────────▶ gleicher Screen, neue appointmentInfo
 *                               (Locale-Refresh nach START, z. B. DE/EN)
 *  *  ──TOKEN_INVALID─────────▶ TECHNICAL_ERROR (generic)
 *  *  ──CUTOFF_REACHED────────▶ BUSINESS_ERROR (TOO_LATE)
 *
 * START ──SUBMIT_CANCEL──▶ LOADING (mit dob)
 * START ──GOTO_LOADING───▶ LOADING (Demo-Switcher, ohne dob)
 *                              │
 *                              ├──GOTO_SUCCESS────────▶ SUCCESS
 *                              └──GOTO_BUSINESS_ERROR─▶ BUSINESS_ERROR
 *
 *  *  ──GOTO_TECHNICAL_ERROR─▶ TECHNICAL_ERROR   (von jedem State erreichbar,
 *                                                  sicherheitskritischer Pfad)
 *  *  ──RESET_TO_START───────▶ START             (Tests & Demo-Switcher)
 * ```
 *
 * Bewusste Designentscheidungen:
 * - `GOTO_TECHNICAL_ERROR`, `TOKEN_INVALID`, `CUTOFF_REACHED` und
 *   `RESET_TO_START` sind von jedem State aus erlaubt — diese Bootstrap-
 *   /Token-Pfade können theoretisch in jedem Lebenszyklus eintreten.
 * - `TOKEN_PARSED` setzt nur von START oder TECHNICAL_ERROR auf START.
 *   Auf LOADING / SUCCESS / BUSINESS_ERROR bleibt der Screen stehen und
 *   es wird nur `appointmentInfo` ersetzt (Locale-Wechsel darf den
 *   laufenden Absage-Flow nicht abbrechen).
 * - Nicht erlaubte Aktionen (z. B. `GOTO_SUCCESS` aus `START`) fallen
 *   stillschweigend auf den aktuellen State zurück — kein Throw, weil der
 *   Reducer pure bleiben muss und Tests so deterministisch sind.
 * - Im `default`-Zweig erzwingt `action satisfies never` die Compile-Zeit-
 *   Erschöpfung: fehlt ein Case, schlägt der Compiler an. Zur Laufzeit fällt
 *   ein falsch typisierter Action-Type ohne Crash auf den aktuellen State
 *   zurück — der Reducer bleibt deterministisch und seiteneffektfrei.
 *
 * @param state Aktueller State.
 * @param action Auszuführende Action.
 */
export function cancelReducer(state: CancelState, action: CancelAction): CancelState {
  switch (action.type) {
    case 'TOKEN_PARSED':
      return applyTokenParsed(state, action.appointmentInfo);
    case 'TOKEN_INVALID':
      return { screen: 'TECHNICAL_ERROR', variant: TechnicalErrorVariant.Generic };
    case 'CUTOFF_REACHED':
      return { screen: 'BUSINESS_ERROR', reason: BusinessErrorReason.TooLate };
    case 'SUBMIT_CANCEL':
      return state.screen === 'START'
        ? withAppointmentInfo({ screen: 'LOADING', dob: action.dob }, state.appointmentInfo)
        : state;
    case 'GOTO_LOADING':
      return state.screen === 'START'
        ? withAppointmentInfo({ screen: 'LOADING' }, state.appointmentInfo)
        : state;
    case 'GOTO_SUCCESS':
      return state.screen === 'LOADING'
        ? withAppointmentInfo({ screen: 'SUCCESS' }, state.appointmentInfo)
        : state;
    case 'GOTO_BUSINESS_ERROR':
      return state.screen === 'LOADING'
        ? withAppointmentInfo(
            { screen: 'BUSINESS_ERROR', reason: action.reason },
            state.appointmentInfo,
          )
        : state;
    case 'GOTO_TECHNICAL_ERROR':
      return { screen: 'TECHNICAL_ERROR', variant: action.variant };
    case 'PINNING_FAILED':
      return {
        screen: 'TECHNICAL_ERROR',
        variant: TechnicalErrorVariant.PinningFailure,
        pinningError: action.pinningError,
      };
    case 'RESET_TO_START':
      return withAppointmentInfo({ screen: 'START' }, extractAppointmentInfo(state));
    default:
      action satisfies never;
      return state;
  }
}

/**
 * Erster Token-Parse (START / TECHNICAL_ERROR) öffnet den Start-Screen.
 * Ein erneutes `TOKEN_PARSED` danach (Locale-Wechsel) ersetzt nur die
 * formatierte Termin-Info — Screen und DOB bleiben, sonst bricht der
 * laufende Submit-/Poll-Flow ab.
 */
function applyTokenParsed(state: CancelState, appointmentInfo: AppointmentInfo): CancelState {
  if (state.screen === 'START' || state.screen === 'TECHNICAL_ERROR') {
    return { screen: 'START', appointmentInfo };
  }
  return { ...state, appointmentInfo };
}

/**
 * Trägt die `appointmentInfo` (sofern vorhanden) in den nächsten State.
 * Bewusst ohne `appointmentInfo: undefined` zu setzen, damit `toEqual`-
 * Assertions ohne diese Property weiter matchen, wenn keine Info da war.
 */
function withAppointmentInfo<TState extends CancelState>(
  next: TState,
  info: AppointmentInfo | undefined,
): TState {
  return info === undefined ? next : { ...next, appointmentInfo: info };
}

/**
 * Liest die `appointmentInfo` aus jedem State, der sie führen kann. Wird
 * vom Reset-Pfad genutzt, damit der Demo-Switcher die Termin-Info beim
 * Sprung zwischen Screens nicht verliert.
 */
function extractAppointmentInfo(state: CancelState): AppointmentInfo | undefined {
  switch (state.screen) {
    case 'START':
    case 'LOADING':
    case 'SUCCESS':
    case 'BUSINESS_ERROR':
      return state.appointmentInfo;
    case 'TECHNICAL_ERROR':
      return undefined;
  }
}
